<template>
  <OrganismAlert
    :title="t('subscription.installerForm.infoAlert.title')"
    class="my-6"
    data-cy="your-installer-alert-info"
    state="info"
  />

  <div data-cy="company-name-input">
    <MoleculeLabel
      :text="t('subscription.installerForm.companyNameInput.label')"
      label-for="companyName"
      class="mb-1"
    />
    <MoleculeInputText
      v-model="companyName"
      id="companyName"
      name="companyName"
      class="w-full"
      :placeholder="t('subscription.installerForm.companyNameInput.placeholder')"
      :disabled="disabled"
      :input-state="isValidationAttempted && getInputState({ errorMessage: errors.companyName })"
      :helper="
        isValidationAttempted &&
        errors.companyName &&
        getHelperMessage({ errorMessage: errors.companyName })
      "
    />
  </div>

  <div data-cy="company-siret-input" class="my-6">
    <MoleculeLabel
      :text="t('subscription.installerForm.companySiretInput.label')"
      label-for="companySiret"
      class="mb-1"
    />
    <MoleculeInputText
      v-model="companySiret"
      id="companySiret"
      name="companySiret"
      class="w-full"
      :disabled="disabled"
      mask="siret"
      :input-state="isValidationAttempted && getInputState({ errorMessage: errors.companySiret })"
      :helper="
        isValidationAttempted &&
        errors.companySiret &&
        getHelperMessage({ errorMessage: errors.companySiret })
      "
    />
  </div>

  <div data-cy="company-email-input">
    <MoleculeLabel
      :text="t('subscription.installerForm.companyEmailInput.label')"
      label-for="companyEmail"
      class="mb-1"
    />
    <MoleculeInputText
      v-model="email"
      id="companyEmail"
      name="companyEmail"
      class="w-full"
      :placeholder="t('subscription.installerForm.companyEmailInput.placeholder')"
      :disabled="disabled"
      :input-state="isValidationAttempted && getInputState({ errorMessage: errors.email })"
      :helper="
        isValidationAttempted && errors.email && getHelperMessage({ errorMessage: errors.email })
      "
    />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useForm } from 'vee-validate';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import useInputState from '@/composables/useInputState.composable';
import { useSubscriptionStore } from '@/stores';

import { siretRegex } from '@/utils/regex.util';

withDefaults(defineProps<{ disabled?: boolean }>(), { disabled: false });

const { t } = useI18n();
const subscriptionStore = useSubscriptionStore();
const { getInputState, getHelperMessage } = useInputState();

const { isGetSubscriptionSuccessfully, getCurrentSubscriptionInstallation } =
  storeToRefs(subscriptionStore);

const isValidationAttempted = ref(false);

const installerSchema = yup.object({
  companyName: yup.string().required(t('common.forms.required')),
  companySiret: yup
    .string()
    .required(t('common.forms.required'))
    .matches(siretRegex, t('common.forms.format.siret')),
  email: yup.string().email(t('common.forms.invalidFormat')).required(t('common.forms.required')),
});

const { errors, defineField, validate } = useForm({ validationSchema: installerSchema });

const [companyName] = defineField('companyName');
const [companySiret] = defineField('companySiret');
const [email] = defineField('email');

const setDefaultValues = () => {
  const installer = getCurrentSubscriptionInstallation.value?.professionalInstaller;
  companyName.value = installer?.companyName || '';
  companySiret.value = installer?.companySiret || '';
  email.value = installer?.email || '';
};

const validateInstaller = () => {
  isValidationAttempted.value = true;
  return validate();
};

const getValues = () => ({
  companyName: companyName.value,
  companySiret: companySiret.value,
  email: email.value,
});

watch(isGetSubscriptionSuccessfully, isSuccessfully => {
  if (isSuccessfully) {
    setDefaultValues();
  }
});

onMounted(() => {
  setDefaultValues();
});

defineExpose({ validate: validateInstaller, getValues });
</script>

<style scoped></style>
